import { StateCreator } from 'zustand';
import { logger } from '@/utils/logger';
import { CanvasImage } from './creativeHistorySlice';
import { WhiskState, CreativeControlsSlice } from './creativeControlsSlice';
import { HistoryItem } from '@/core/types/history';
import type { StoreState } from '@/core/store/types';

export interface DesignVersionState {
    studioControls: CreativeControlsSlice['studioControls'];
    canvasImages: CanvasImage[];
    whiskState: WhiskState;
    characterReferences: Array<{ image: HistoryItem; referenceType: 'subject' | 'style' | 'reference'; name?: string }>;
    creativePrompt: string;
}

export interface DesignVersion {
    id: string;
    name: string;
    createdAt: number;
    projectId: string;
    userId: string;
    state: DesignVersionState;
}

export interface DesignHistorySlice {
    designVersions: DesignVersion[];
    // ISSUE-1395 (audit): now returns whether the version was persisted
    // durably to Firestore — callers (flatten) rely on a cloud revision for
    // reload recovery and must not believe one exists when the write failed
    // (full-res data-URI canvas states can exceed the 1 MiB document limit,
    // and Firestore errors used to be swallowed).
    saveDesignVersion: (name?: string) => Promise<boolean>;
    restoreDesignVersion: (version: DesignVersion) => void;
    deleteDesignVersion: (id: string) => Promise<void>;
    initializeDesignHistory: () => Promise<void>;
}

export function buildDesignHistoryState(
    set: Parameters<StateCreator<StoreState, [], [], DesignHistorySlice>>[0],
    get: Parameters<StateCreator<StoreState, [], [], DesignHistorySlice>>[1]
): DesignHistorySlice {
    return {
        designVersions: [],
        
        saveDesignVersion: async (name) => {
            const state = get();
            const { currentProjectId, studioControls, canvasImages, whiskState, characterReferences, creativePrompt } = state;
            
            if (!currentProjectId) {
                logger.error("DesignHistory: No project selected");
                return false;
            }

            const { getAuth } = await import('firebase/auth');
            const userId = getAuth().currentUser?.uid;
            if (!userId) {
                throw new Error('DesignHistory: User must be authenticated to save a design version');
            }

            const newVersion: DesignVersion = {
                id: `version_${Date.now()}`,
                name: name || `Version ${new Date().toLocaleString('en-US')}`,
                createdAt: Date.now(),
                projectId: currentProjectId,
                userId,
                state: {
                    studioControls: JSON.parse(JSON.stringify(studioControls)), // Deep clone
                    canvasImages: JSON.parse(JSON.stringify(canvasImages)),
                    whiskState: JSON.parse(JSON.stringify(whiskState)),
                    characterReferences: JSON.parse(JSON.stringify(characterReferences)),
                    creativePrompt
                }
            };

            set((state: StoreState) => ({
                designVersions: [newVersion, ...state.designVersions]
            }));

            // Persistence
            try {
                const { FirestoreService } = await import('@/services/FirestoreService');
                const service = new FirestoreService<DesignVersion>('design_versions');
                await service.set(newVersion.id, newVersion);
                logger.info("DesignHistory: Saved version", newVersion.id);
                return true;
            } catch (err) {
                logger.error("DesignHistory: Failed to save version", err);
                return false;
            }
        },

        // ISSUE-1395 (audit): restoring a version saved under a different
        // project used to overwrite the current board unconditionally.
        // Reject mismatched versions and re-stamp restored images to the
        // current project so board state can never cross projects.
        restoreDesignVersion: (version) => {
            const currentProjectId = get().currentProjectId;
            if (currentProjectId && version.projectId && version.projectId !== currentProjectId) {
                logger.error('DesignHistory: Refusing to restore a version from another project', {
                    versionProjectId: version.projectId,
                    currentProjectId,
                });
                import('@/core/events').then(({ events }) => {
                    events.emit('SYSTEM_ALERT', {
                        level: 'error',
                        message: 'This design version belongs to another project and was not restored.',
                    });
                }).catch(() => { /* events module unavailable in some test contexts */ });
                return;
            }
            set((state: StoreState) => ({
                studioControls: { ...state.studioControls, ...version.state.studioControls },
                canvasImages: (version.state.canvasImages || []).map(img => ({
                    ...img,
                    projectId: currentProjectId || img.projectId,
                })),
                whiskState: { ...state.whiskState, ...version.state.whiskState },
                characterReferences: [...version.state.characterReferences],
                creativePrompt: version.state.creativePrompt
            }));
            logger.info("DesignHistory: Restored version", version.id);
        },

        deleteDesignVersion: async (id) => {
            set((state: StoreState) => ({
                designVersions: state.designVersions.filter((v) => v.id !== id)
            }));

            try {
                const { FirestoreService } = await import('@/services/FirestoreService');
                const service = new FirestoreService<DesignVersion>('design_versions');
                await service.delete(id);
                logger.info("DesignHistory: Deleted version", id);
            } catch (err) {
                logger.error("DesignHistory: Failed to delete version", err);
            }
        },

        initializeDesignHistory: async () => {
            const projectId = get().currentProjectId;
            if (!projectId) return;

            try {
                const { FirestoreService } = await import('@/services/FirestoreService');
                const { where } = await import('firebase/firestore');
                const service = new FirestoreService<DesignVersion>('design_versions');
                
                const { getAuth } = await import('firebase/auth');
                const userId = getAuth().currentUser?.uid;
                if (!userId) {
                    set({ designVersions: [] });
                    logger.warn("DesignHistory: User must be authenticated to initialize design history");
                    return;
                }

                const versions = await service.list([
                    where('projectId', '==', projectId),
                    where('userId', '==', userId)
                ]);

                versions.sort((a, b) => b.createdAt - a.createdAt);

                set({ designVersions: versions });
                logger.info("DesignHistory: Initialized with", versions.length, "versions");
            } catch (err) {
                logger.error("DesignHistory: Failed to initialize", err);
            }
        }
    };
}

